/**
 * Keeps reusable blocklist creation behind its dedicated permission without
 * changing feature-flagged route access.
 */
import React from 'react';
import { useNavigate } from 'react-router';
import { useForm, FormProvider } from 'react-hook-form';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Row from '@amzn/meridian/row';
import { useTranslation } from 'src/arb-wrapper';
import { FormInput } from 'src/components/common/forms';
import { FormGtiInput } from 'src/components/common/forms/FormGtiInput';
import { GTI_BLOCKLIST_MAX, ConfigurationType } from 'src/constants';
import { useCampaignConfigurationControllerCreateMutation, useCatalogControllerGetCatalogTitlesMutation } from 'src/store/leela-api';
import { AlertType } from 'src/components/common/alerts/alert.types';
import { useToasterContext } from 'src/components/common/alerts/ToasterContext';
import { useCanPerform } from 'src/hooks/useCanPerform';
import { useGtiValidation } from 'src/hooks/useGtiValidation';
import { parseGtisFromField } from 'src/utils';

export const BlocklistCreate: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { createToast } = useToasterContext();
  const canManageBlocklists = useCanPerform('MANAGE_BLOCKLISTS');

  const [createConfig, { isLoading: isCreating }] = useCampaignConfigurationControllerCreateMutation();
  const [fetchTitles, { data: titles, isLoading: isFetchingTitles }] = useCatalogControllerGetCatalogTitlesMutation();

  const methods = useForm<{
    name: string;
    gtis: string;
  }>({
    defaultValues: {
      name: '',
      gtis: '',
    },
    mode: 'onChange',
  });

  const { control, handleSubmit, watch, formState } = methods;

  const watchedGtis = watch('gtis');

  const gtiTitleMap = React.useMemo(() => {
    if (!titles) return new Map();
    return new Map(
      titles.map(title => [
        title.gti ?? '',
        { title: title.title, coverImageUrl: title.coverImageUrl ?? undefined },
      ])
    );
  }, [titles]);

  // Use GTI validation hook for parsing
  const { parseValidGtiList } = useGtiValidation({
    value: watchedGtis || '',
    gtiTitleMap,
  });

  React.useEffect(() => {
    if (watchedGtis) {
      const gtiList = parseValidGtiList(watchedGtis);

      if (gtiList.length > 0) {
        void fetchTitles({
          getCatalogGtiRequest: {
            gtiList,
          },
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedGtis, fetchTitles]);

  const onSubmit = async (formData: {
    name: string;
    gtis: string;
  }) => {
    try {
      const gtiList = parseGtisFromField(methods.getValues('gtis_valid' as never) ?? '');

      const payload: {
        name: string;
        config: {
          configType: ConfigurationType.GTI_BLOCKLIST;
          gtis: string[];
        };
      } = {
        name: formData.name,
        config: {
          configType: ConfigurationType.GTI_BLOCKLIST,
          gtis: gtiList,
        },
      };

      const result = await createConfig({
        createCampaignConfigurationDto: payload,
      }).unwrap();

      createToast({
        type: AlertType.SUCCESS,
        message: t('blocklist_create_success'),
      });

      navigate(`/blocklists/${result.id}`);
    } catch (error: unknown) {
      const err = error as { data?: { message?: string }; message?: string };
      const errorMessage = err?.data?.message ?? err?.message ?? t('blocklist_create_error');
      createToast({
        type: AlertType.ERROR,
        message: errorMessage,
      });
    }
  };

  if (!canManageBlocklists) {
    return (
      <Column backgroundColor="default" spacing="400">
        <Heading level={1}>{t('blocklist_create')}</Heading>
      </Column>
    );
  }

  return (
    <Column backgroundColor="default" spacing="400">
      <Heading level={1}>{t('blocklist_create')}</Heading>

      <Box backgroundColor="default" spacingInset="none 400 400 none">
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Column spacing="400">
            <Heading level={3}>{t('program_header_general_settings')}</Heading>

            <Row spacing="600">
              <Column width="fill">
                <FormInput
                  // @ts-expect-error - Control type mismatch with react-hook-form
                  control={control}
                  label={t('form_global_name')}
                  name="name"
                  placeholder="Enter blocklist name"
                  required
                />
              </Column>
            </Row>

            <Heading level={3}>{t('form_campaign_titles_label')}</Heading>

            <FormGtiInput
              // @ts-expect-error - Control type mismatch with react-hook-form
              control={control}
              gtiTitleMap={gtiTitleMap}
              isFetching={isFetchingTitles}
              label=""
              maxGtis={GTI_BLOCKLIST_MAX}
              name="gtis"
              requireCoverImage={false}
              requireMinimum={false}
              required
              showInvalidFilteredWarning
              showTitleColumn
            />

            <Row alignmentHorizontal="end" spacing="200">
              <Button
                type="secondary"
                onClick={() => {
                  navigate('/blocklists');
                }}
              >
                {t('form_global_cancel')}
              </Button>
              <Button
                disabled={!formState.isValid || isCreating}
                type="primary"
                onClick={handleSubmit(onSubmit)}
              >
                {t('button_submit')}
              </Button>
            </Row>
          </Column>
        </form>
        </FormProvider>
      </Box>
    </Column>
  );
};
